import chroma from 'chroma-js';
import { getHashParam } from './hashParams';
const layers = {
    subregions: {
        lineId: 'subregion-line', // id of the line layer
        fillId: 'subregion-fill',
        // fillId is the id of the fill layer, if there is one
        labelId: 'subregion-labels', // id of the label layer (used to add click handlers)

        // these colors are applied to the line, fill and label layers when regions are selected by being clicked/searched for
        // color: 'hsl(0, 58%, 50%)', // normal color for the layer, used when setting back after being faded/highlighted
        // highlightColor: 'hsl(0, 68%, 50%)', // highlight color is applied to the selected region in this layer
        // fadeColor: 'hsl(0, 35%, 65%)', // fade color is applied to all the regions in this layer that *aren't* selected when there is a selected region
    },
    mid: {
        lineId: 'middle-line',
        fillId: 'middle-fill',
        labelId: 'middle-labels',
    },
    top: {
        lineId: 'top-line',
        fillId: 'top-fill',
        labelId: 'top-labels',
    },
};

function extractColors(map) {
    function hsl(chromaColor) {
        const r = (v) => Math.round((v || 0) * 10) / 10;
        const [h, s, l] = chromaColor.hsl();

        return `hsl(${r(h || 0)}, ${r(100 * s)}%, ${r(100 * l)}%)`;
    }
    function adjustColor(color, adjustment) {
        if (adjustment === 'highlight') {
            return hsl(
                chroma(color)
                    .saturate(2)
                    .darken(1)
            );
        } else {
            return hsl(
                chroma(color)
                    .desaturate(1)
                    .brighten(0.5)
            );
        }
    }
    function adjustProp(color, adjustment) {
        if (typeof color === 'string') {
            return adjustColor(color, adjustment);
        } else if (Array.isArray(color)) {
            return color.map((c) => {
                if (typeof c === 'string' && chroma.valid(c)) {
                    return adjustColor(c, adjustment);
                } else {
                    return c;
                }
            });
        } else {
            console.error("Can't adjust color ${color}");
        }
    }
    function getAdjustedColor(baseId, prop, adjustment) {
        const adjustedLayerId = `${baseId}-${adjustment}`; // middle-fill-highlight
        const adjustedLayerStyle = map.U.getLayerStyle(adjustedLayerId);
        if (
            adjustedLayerStyle &&
            !(
                adjustedLayerStyle.layout &&
                adjustedLayerStyle.visibility === 'none'
            )
        ) {
            return adjustedLayerStyle.paint[prop];
        } else {
            const baseLayerStyle = map.U.getLayerStyle(baseId);
            return adjustProp(baseLayerStyle.paint[prop], adjustment);
        }
    }
    for (const level of Object.keys(layers)) {
        for (const adjustment of ['highlight', 'fade']) {
            const layer = layers[level];
            const Adjustment = { highlight: 'Highlight', fade: 'Fade' }[
                adjustment
            ];
            if (layer.fillId) {
                layer[`fill${Adjustment}Color`] = getAdjustedColor(
                    layer.fillId,
                    'fill-color',
                    adjustment
                );
            }
            layer[`line${Adjustment}Color`] = getAdjustedColor(
                layer.lineId,
                'line-color',
                adjustment
            );
            layer[`label${Adjustment}Color`] = getAdjustedColor(
                layer.labelId,
                'text-color',
                adjustment
            );
        }
    }
    window.app.layers = layers;
}

function extractFilters(map) {
    for (const level of Object.keys(layers)) {
        for (const type of ['line', 'fill', 'label']) {
            layers[level][`${type}Filter`] =
                map.U.getLayerStyle(layers[level][type + 'Id']).filter || true;
        }
    }
    console.log(layers);
}

function makeAdjustmentLayers(map) {
    extractFilters(map);
    extractColors(map);
    for (const layer of Object.values(layers)) {
        // line hit
        map.addLayer({
            ...map.U.getLayerStyle(layer.lineId),
            paint: {
                'line-color': 'transparent',
                'line-width': 15,
            },
            layout: {
                'line-cap': 'round',
            },
            id: `${layer.lineId} hit`,
            filter: true,
        });
        for (const adjustment of ['highlight', 'fade']) {
            const propSuffix =
                adjustment === 'highlight' ? 'HighlightColor' : 'FadeColor';
            // fill adjustment
            if (layer.fillId) {
                map.addLayer(
                    map.U.properties({
                        ...map.U.getLayerStyle(layer.fillId),
                        id: `${layer.fillId} ${adjustment}`,
                        filter: false,
                    }),
                    adjustment === 'fade' ? layer.fillId : undefined
                );
                map.U.setFillColor(
                    `${layer.fillId} ${adjustment}`,
                    layer[`fill${propSuffix}`]
                );
                if (adjustment === 'highlight') {
                    if (!layer.fillId.match(/^top|mid/)) {
                        map.U.setFillOpacity(
                            `${layer.fillId} ${adjustment}`,
                            getHashParam('highlight-fill-opacity', [
                                'interpolate',
                                ['linear'],
                                ['zoom'],
                                8,
                                0.3,
                                10,
                                0,
                            ])
                        );
                    } else {
                        map.U.setFillOpacity(
                            `${layer.fillId} ${adjustment}`,
                            0
                        );
                    }
                }
            }

            // line adjustment
            map.addLayer(
                map.U.properties({
                    ...map.U.getLayerStyle(layer.lineId),
                    id: `${layer.lineId} ${adjustment}`,
                    filter: false,
                }),
                adjustment === 'fade' ? layer.lineId : undefined
            );
            map.U.setLineColor(
                `${layer.lineId} ${adjustment}`,
                layer[`line${propSuffix}`]
            );
            if (adjustment === 'highlight') {
                map.U.setLineWidth(
                    `${layer.lineId} ${adjustment}`,
                    getHashParam('highlight-line-width', 2)
                );
                map.U.setLineOpacity(`${layer.lineId} ${adjustment}`, 1);
                map.U.show(`${layer.lineId} ${adjustment}`);
                if (layer.fillId.match(/^top/)) {
                    map.U.setLineColor(
                        `${layer.lineId} ${adjustment}`,
                        map.U.getLayerStyle(layer.fillId).paint['fill-color']
                    );
                }
            } else {
                map.U.setLineWidth(`${layer.lineId} ${adjustment}`, 1);
            }
            // label adjustment
            map.addLayer(
                map.U.properties({
                    ...map.U.getLayerStyle(layer.labelId),
                    id: `${layer.labelId} ${adjustment}`,
                    filter: false,
                }),
                adjustment === 'fade' ? layer.labelId : undefined
            );
            map.U.setTextColor(
                `${layer.labelId} ${adjustment}`,
                layer[`label${propSuffix}`]
            );
            if (adjustment === 'highlight') {
                map.U.setTextFont(`${layer.labelId} ${adjustment}`, [
                    'DIN Pro Bold',
                ]);
            }
        }
    }
}

function highlightRegion(map, props) {
    const hasHighlight = !!props;
    const highlightFilter = hasHighlight
        ? [
              'all',
              ['==', ['to-string', ['get', 'country']], props.country || ''],
              ['==', ['to-string', ['get', 'class']], props.class || ''],
              ['==', ['to-string', ['get', 'Name_rgn']], props.Name_rgn || ''],
          ]
        : false;
    const fadeFilter = hasHighlight ? ['!', highlightFilter] : false;
    for (const layer of Object.values(layers)) {
        for (const type of ['line', 'fill', 'label']) {
            const baseId = layer[type + 'Id'];

            map.U.setFilter(
                baseId,
                hasHighlight ? false : layer[type + 'Filter']
            );
            map.U.setFilter(baseId + ' highlight', highlightFilter); // do these need to include base filter?
            map.U.setFilter(baseId + ' fade', fadeFilter);
        }
    }
}

const regionLineLayers = Object.values(layers).map((l) => l.lineId);
const regionLineHitLayers = regionLineLayers.map((l) => `${l} hit`);

const interactiveLayers = [
    ...regionLineLayers,
    ...regionLineHitLayers,
    ...Object.values(layers).map((l) => l.labelId),
    ...Object.values(layers).map((l) => l.labelId + ' highlight'),
    ...Object.values(layers).map((l) => l.lineId + ' highlight'),
    ...Object.values(layers).map((l) => l.labelId + ' fade'),
    ...Object.values(layers).map((l) => l.lineId + ' fade'),
    // 'wine country lbl', // disable clicking on countries for now.
];

export {
    layers,
    makeAdjustmentLayers as makeHighlightLayers,
    extractColors,
    highlightRegion,
    interactiveLayers,
};
